import { APIDetails } from './apis-details';

export const aiTutorApi: APIDetails = {
  id: 'API_KEYS.aiTutorApi',
  title: 'AI Tutor API',
  subtitle:
    'AI-powered course creation, review, and enhancement for educational platforms.',
  description: [
    'Educational platforms and content creators often struggle to produce high-quality, comprehensive courses efficiently. Creating structured modules, reviewing content for accuracy and depth, and ensuring industry relevance can be time-consuming and inconsistent.',
    'The AI Tutor API review course using AI. With simple API calls, platforms can generate course outlines, structured modules, summaries, and receive AI-driven expert reviews. The API evaluates strengths, identifies gaps, and suggests improvements, enabling educators to deliver high-quality, engaging, and industry-relevant learning experiences without manual effort.',
    'Whether you are building an LMS, online course platform, or tutoring tool, the AI Tutor API allows scalable, data-driven course creation with actionable insights.',
  ],
  overview: [
    'The AI Tutor API is a RESTful service that allows educational platforms to:',
    '• Track AI-generated review progress',
    '• Retrieve expert evaluation of courses',
    '• Access module-level analysis and recommendations',
  ],
  features: [
    {
      title: 'AI Expert Review',
      description:
        'AI evaluates course structure, module content, difficulty, and coverage',
    },
    {
      title: 'Module-level Feedback',
      description:
        'Get insights on strengths, weaknesses, and suggested improvements',
    },
    {
      title: 'Learning Outcome Evaluation',
      description:
        'Verify course alignment with expected skills and competencies',
    },
    {
      title: 'Actionable Recommendations',
      description: 'Improve course quality, engagement, and industry relevance',
    },
  ],
  workflow: [
    'Educator/platform requests course creation via API',
    'System generates expert review, module-level scores, strengths, lacking points, and suggestions',
    'API saves review status',
    'Educator checks course review progress via status endpoint',
    'Completed course data, expert review, and improvement suggestions are retrieved',
    'Educator can integrate feedback to enhance course quality',
  ],
  endpoints: [
    {
      category: 'Course Management',
      items: [
        {
          category: 'Create Course Instance',
          method: 'POST',
          path: '/api/v1/course/create',
          description: 'Creates a course instance',
        },
        {
          category: 'Get Course Status',
          method: 'GET',
          path: '/api/v1/course/status/{course_id}',
          description:
            'Retrieves current status of AI-generated course review and processing',
        },
        {
          category: 'Get Course Review',
          method: 'GET',
          path: '/api/v1/course/get/{course_id}',
          description:
            'Returns detailed AI review including module scores, strengths, weaknesses, suggested improvements, and learning outcomes',
        },
        {
          category: 'Remove Course',
          method: 'DELETE',
          path: '/api/v1/course/delete/{course_id}',
          description:
            'Deletes course related data including CourseDetails, CourseTask, Module, and Course records',
        },
      ],
    },
  ],
  apiDocumentation: [
    // Course Endpoints
    {
      title: 'Create Course Instance',
      method: 'POST',
      path: '/api/v1/course/create',
      requestPath: 'POST /api/v1/course/create',
      description: 'Creates a course instance',
      requestExample: {
        title: 'Comprehensive Data Science Bootcamp',
        summary:
          'Covers Python, statistics, machine learning, data visualization, and deployment',
        modules: [
          {
            name: 'Introduction to Data Science',
            summary: 'Overview of data science, tools, and workflow',
            expectation: 'Understand the data science lifecycle and tools',
          },
          {
            name: 'Python for Data Science',
            summary: 'Python basics, pandas, NumPy, and data manipulation',
            expectation: 'Write Python scripts for data analysis',
          },
          {
            name: 'Statistics & Probability',
            summary:
              'Descriptive stats, probability distributions, hypothesis testing',
            expectation: 'Perform statistical analysis on datasets',
          },
          {
            name: 'Machine Learning Fundamentals',
            summary: 'Supervised and unsupervised learning, model evaluation',
            expectation: 'Train and evaluate ML models using Python',
          },
          {
            name: 'Data Visualization',
            summary: 'Matplotlib, Seaborn, Plotly for visual storytelling',
            expectation: 'Create insightful charts and dashboards',
          },
          {
            name: 'Project & Deployment',
            summary:
              'End-to-end data science project with deployment using Flask/Streamlit',
            expectation: 'Deploy a data science model to a web application',
          },
        ],
      },
      responseExample: {
        course_id: 'c8b2d9f1-7c34-4f41-9b52-8a7b9f2a1c4e',
        processing_status: 'IN_PROGRESS',
        created_at: '2025-01-01T10:30:00Z',
        message: 'Course created successfully. AI analysis in progress.',
      },
      statusCode: '202',
    },
    {
      title: 'Get Course Review Status',
      method: 'GET',
      path: '/api/v1/course/status/{course_id}',
      requestPath: 'GET /api/v1/course/status/{course_id}',
      description:
        'Retrieves current status of AI-generated course review and processing',
      requestExample: {
        course_id: 'c8b2d9f1-7c34-4f41-9b52-8a7b9f2a1c4e',
      },
      responseExample: {
        course_id: 'c8b2d9f1-7c34-4f41-9b52-8a7b9f2a1c4e',
        processing_status: 'COMPLETED',
        updated_at: '2025-01-01T10:35:45Z',
      },
      statusCode: '200',
    },
    {
      title: 'Get Course Expert Analysis',
      method: 'GET',
      path: '/api/v1/course/{course_id}/analysis',
      requestPath: 'GET /api/v1/course/{course_id}/analysis',
      description:
        'Returns detailed AI review including module scores, strengths, weaknesses, suggested improvements, and learning outcomes',
      requestExample: {
        course_id: 'c8b2d9f1-7c34-4f41-9b52-8a7b9f2a1c4e',
      },
      responseExample: {
        expert_review:
          "This course, titled 'Comprehensive Data Science Bootcamp,' is significantly mislabeled given its current module structure...",
        strength:
          'Strong focus on end-to-end project deployment using Flask/Streamlit.',
        lacking:
          'Missing deep coverage of statistics, machine learning, data visualization, SQL, and feature engineering.',
        considerations:
          'Add dedicated modules for statistics, ML, visualization, SQL, and cloud platforms.',
        score: 4.5,
        difficulty_level: 'Intermediate',
        industry_relevance: 7,
        engagement_score: 6.5,
        estimated_time: '6 weeks (10–12 hrs/week)',
        module_scores: {
          'Introduction to Data Science': 6,
          'Python for Data Science': 7,
          'Project & Deployment': 7.5,
        },
        module_details: [
          {
            title: 'Introduction to Data Science',
            difficulty: 1,
            strengths: 'Clear overview of data science workflow.',
            improvements: 'Add real-world case studies.',
            suggested_quiz: {
              question:
                'Which is the first step in the data science lifecycle?',
              choices: [
                'Model Deployment',
                'Data Collection',
                'Data Cleaning',
                'Model Training',
              ],
              answer_index: 1,
            },
          },
        ],
        learning_outcomes: [
          'Understand data science workflows',
          'Analyze data using Python',
          'Deploy models as web applications',
        ],
        prerequisites: [
          'Basic programming knowledge',
          'High-school level mathematics',
        ],
        improvements: [
          'Add Statistics module',
          'Add Machine Learning module',
          'Add SQL and Data Visualization modules',
          'Expand deployment with cloud & MLOps',
        ],
      },
      statusCode: '200',
    },
    {
      title: 'Remove Course',
      method: 'DELETE',
      path: '/api/v1/course/delete/{course_id}',
      requestPath: 'DELETE /api/v1/course/delete/{course_id}',
      description:
        'Deletes course related data including CourseDetails, CourseTask, Module, and Course records',
      requestExample: {
        course_id: 'c8b2d9f1-7c34-4f41-9b52-8a7b9f2a1c4e',
      },
      responseExample: {
        message: 'Tutor deleted successfully',
      },
      statusCode: '200',
    },
  ],
  useCases: [
    {
      title: 'Online Course Platforms',
      description:
        'Quickly improve high-quality courses with structured modules and expert-level feedback.',
    },
    {
      title: 'Learning Management Systems (LMS)',
      description:
        'Ensure consistent course quality across large content libraries without manual review.',
    },
    {
      title: 'EdTech Startups',
      description:
        'Launch new courses faster while maintaining industry relevance and strong learning outcomes.',
    },
    {
      title: 'Corporate Training Programs',
      description:
        'Review and optimize internal training content to align with required skills and competencies.',
    },
    {
      title: 'Independent Educators & Content Creators',
      description:
        'Get expert-like course reviews and improvement suggestions without hiring reviewers.',
    },
    {
      title: 'University & Academic Portals',
      description:
        'Standardize curriculum quality and validate course coverage across departments.',
    },
    {
      title: 'Upskilling & Reskilling Platforms',
      description:
        'Continuously refine learning paths to meet evolving industry demands.',
    },
    {
      title: 'Certification & Bootcamp Providers',
      description:
        'Improve course depth, structure, and engagement to boost learner success rates.',
    },
  ],
  targetUsers: [
    'EdTech SaaS companies',
    'Online course platforms',
    'AI tutoring systems',
    'Universities & training institutes',
    'Enterprise learning teams',
  ],
};
